import FeatherIcon from "feather-icons-react";
import { useEffect,useState } from "react";
import {
  Card,
  CardHeader,
  CardBody,
  Modal,
  Col,
  Row,
  Spinner,
  Input,
  Button,
} from "reactstrap";
import { motion, AnimatePresence } from "framer-motion";
import api from "../helpers/api";
import * as XLSX from "xlsx";
import Select from "react-select";
import FileModal from "../components/Common/importExportModal";
import { countryOptions } from "../constants/utils";
import PermissionGuard from '../components/Common/PermissionGuard';

const Organization = () => {
  document.title = "Organizations | Arcmedix";
  const [modal_large, setmodal_large] = useState(false);
  const [selectedEntry, setSelectedEntry] = useState(null);
  const [organizations, setOrganizations] = useState([]);
  const [errors, setErrors] = useState({});
  const [apiError, setApiError] = useState("");
  const [imagePopup, setImagePopup] = useState({
    isOpen: false,
    imageUrl: "",
  });
  const [count, setCount] = useState(10);
  const [loading, setLoading] = useState(false);
  const [hasMore, setHasMore] = useState(true);
  const [fileModal, setFileModal] = useState(false);
  const [searchTerm, setSearchTerm] = useState("");

  function tog_large(entry = null) {
    setmodal_large(!modal_large);
    setSelectedEntry(entry || {});
    setErrors({});
    setApiError("");
  }

  const handleModalClose = () => {
    setmodal_large(false);
    setSelectedEntry(null);
  };

  const openImagePopup = (imageUrl) => {
    setImagePopup({
      isOpen: true,
      imageUrl: imageUrl,
    });
  };

  const closeImagePopup = () => {
    setImagePopup({
      isOpen: false,
      imageUrl: "",
    });
  };

  const handleFileChange = async (e) => {
    const file = e.target.files[0];
    if (!file) return;

    try {
      const publicURL = await getPresignedUrl(file);
        setSelectedEntry({ ...selectedEntry, profileUrl: publicURL });
    } catch (err) {
      setApiError("❌ Error during upload:", err);
    }
  };

const getPresignedUrl = async (file) => {
  const formData = new FormData();

  formData.append("files", file);

  try {
    const response = await api.post("files/img-upload", formData, {
      headers: {
        "Content-Type": "multipart/form-data",
      },
    });

    const presignedUrl = response.data?.response[0]?.publicUrl;
    return presignedUrl;
  } catch (err) {
    console.error("Error getting presigned URL:", err);
    return null;
  }
};


  const handleUpdate = async (e) => {
    e.preventDefault();

    const newErrors = {};
    if (!selectedEntry?.name?.trim())
      newErrors.name = "Organization name is required";
    if (!selectedEntry?.address?.trim())
      newErrors.address = "Address is required";
    if (!selectedEntry?.email?.trim())
      newErrors.email = "Email Address is required";
    if (!selectedEntry?.contactNumber?.trim())
      newErrors.contactNumber = "Contact number is required";

    if (Object.keys(newErrors).length > 0) {
      setErrors(newErrors);
      return;
    }

    try {
      setApiError("");
      const isCreate = !selectedEntry?.id;
      const endpoint = isCreate ? "organization/create" : "organization/update";
      const payload = isCreate ? [selectedEntry] : selectedEntry;

      await api.post(endpoint, payload);

      reloadOrganizations();
      handleModalClose();
    } catch (error) {
      setApiError(error?.response?.data?.error || "Something went wrong!");
    }
  };

  const handleImport = async (file) => {
    try {
      setLoading(true);

      // Read the Excel file
      const reader = new FileReader();
      reader.onload = async (e) => {
        const data = new Uint8Array(e.target.result);
        const workbook = XLSX.read(data, { type: "array" });
        const firstSheetName = workbook.SheetNames[0];
        const worksheet = workbook.Sheets[firstSheetName];
        const jsonData = XLSX.utils.sheet_to_json(worksheet);

        // Transform the imported data to match the organization structure
        const organizationsToImport = jsonData
          .map((item) => ({
            name: item["Name"] || "",
            address: item["Address"] || "",
            email: item["Email"] || "",
            contactNumber: item["Contact Number"] || "",
          }))
          .filter((organization) => organization.name); // Filter out empty rows

        if (organizationsToImport.length === 0) {
          setApiError("No valid organization data found in the file");
          return;
        }

        try {
          // Make API call to create organizations in bulk
          await api.post("organization/create", organizationsToImport);

          // Refresh the organization list
          reloadOrganizations();
          setFileModal(false);
        } catch (error) {
          setApiError(
            error?.response?.data?.error || "Failed to import organizations"
          );
        }
      };

      reader.readAsArrayBuffer(file);
    } catch (error) {
      setApiError("Error processing the file");
      console.error("Import error:", error);
    } finally {
      setLoading(false);
    }
  };

  const handleExport = () => {
    const dataToExport =
      organizations?.map((organization) => ({
        "#": organizations.indexOf(organization) + 1,
        Name: organization.name || "",
        Address: organization.address || "",
        Email: organization.email || "",
        "Contact Number": organization.contactNumber || "",
      })) || [];

    const ws = XLSX.utils.json_to_sheet(dataToExport);
    const wb = XLSX.utils.book_new();
    XLSX.utils.book_append_sheet(wb, ws, "Organizations");
    XLSX.writeFile(
      wb,
      `organizations_${new Date().toISOString().split("T")[0]}.xlsx`
    );
  };

  const handleClose = () => {
    setFileModal(false);
    setApiError("");
  };

  const reloadOrganizations = async () => {
    setLoading(true);
    try {
      const { data } = await api.get(
        `organization/all?count=${count}${
          searchTerm ? `&search=${searchTerm}` : ""
        }`
      );
      setOrganizations(data.response || []);
      setHasMore(data.response.length >= count);
    } catch (error) {
      console.error("Error reloading organizations:", error);
    } finally {
      setLoading(false);
    }
  };

  const loadMoreOrganizations = async () => {
    if (!hasMore) return;
    
    setLoading(true);
    try {
      const newCount = count + 10;
      const { data } = await api.get(
        `organization/all?count=${newCount}${
          searchTerm ? `&search=${searchTerm}` : ""
        }`
      );
      
      setOrganizations(data.response || []);
      setCount(newCount);
      setHasMore(data.response.length >= newCount);
    } catch (error) {
      console.error("Error loading more organizations:", error);
    } finally {
      setLoading(false);
    }
  };

  const handleSearch = (e) => {
    const term = e.target.value;
    setSearchTerm(term);
    setCount(10);
    setHasMore(true);
  };

  useEffect(() => {
    reloadOrganizations();
  }, [searchTerm]);

  return (
    <div className="page-content mt-4">
      <div className="mb-4 d-flex align-items-center gap-3">
        <motion.div
          className="mb-4 d-flex align-items-center gap-3"
          initial={{ opacity: 0, y: -10 }}
          animate={{ opacity: 1, y: 0 }}
          transition={{ duration: 0.4 }}
        >
          <PermissionGuard permission="organization:create">
            <button
              type="button"
              className="btn btn-soft-success waves-effect waves-light d-flex align-items-center gap-2 transition-all hover-shake"
              onClick={() => {
                tog_large();
              }}
            >
              <svg
                xmlns="http://www.w3.org/2000/svg"
                viewBox="0 0 24 24"
                fill="currentColor"
                width={21}
                height={21}
              >
                <path d="M3 22V10.243a1 1 0 0 1 .447-.832l8-5.333a1 1 0 0 1 1.106 0l8 5.333A1 1 0 0 1 21 10.243V22h-6v-6h-6v6H3zM11 13h2v2h-2v-2zm0 4h2v2h-2v-2zm4-4h2v2h-2v-2zm0 4h2v2h-2v-2zm-8-4h2v2H7v-2zm0 4h2v2H7v-2z" />
              </svg>
              Add Organization
            </button>
          </PermissionGuard>

          <div
            className="ms-auto"
            style={{ minWidth: "400px", flexGrow: 1, maxWidth: "500px" }}
          >
            <Input
              type="text"
              placeholder="Search organizations..."
              value={searchTerm}
              onChange={handleSearch}
            />
          </div>
        </motion.div>
      </div>

      <Card>
        <CardHeader className="d-flex justify-content-between align-items-center">
          <h3 className="card-title text-success">Organizations</h3>
          <div className="d-flex align-items-center gap-2">
            <button
              className="btn btn-soft-info p-2"
              onClick={() => setFileModal(true)}
              title="Import/Export transactions"
            >
              <FeatherIcon icon="file-text" size={18} />
            </button>
          </div>
        </CardHeader>

        <CardBody>
          <div
            className="table-responsive"
            style={{
              overflowX: "auto",
              whiteSpace: "nowrap",
            }}
          >
            <table
              className="table table-hover mb-0"
              style={{
                borderSpacing: "0.1rem",
                borderCollapse: "separate",
              }}
            >
              <thead>
                <tr>
                  <th>#</th>
                  <th>Profile</th>
                  <th>Name</th>
                  <th>Address</th>
                  <th>Email</th>
                  <th>Contact Number</th>
                  <th>Status</th>
                  <th>Seats</th>
                  <th>Edit</th>
                  <th>Delete</th>
                </tr>
              </thead>
              <tbody>
                <AnimatePresence>
                  {organizations?.map((organization, ind) => (
                    <motion.tr
                      key={organization?.id}
                      initial={{ opacity: 0, y: 20 }}
                      animate={{ opacity: 1, y: 0 }}
                      exit={{ opacity: 0, y: -20 }}
                      transition={{ duration: 0.3 }}
                    >
                      <th scope="row">{ind + 1}</th>
                      <th scope="row">
                        <img
                          alt="profile-pic"
                          src={organization?.profileUrl}
                          height={27}
                          width={27}
                          style={{ cursor: "pointer" }}
                          onClick={() =>
                            organization?.profileUrl &&
                            openImagePopup(organization.profileUrl)
                          }
                        />
                      </th>
                      <td>{organization?.name}</td>
                      <td>{organization?.address}</td>
                      <td>{organization?.email}</td>
                      <td>{organization?.contactNumber}</td>
                      <td>
                        {organization?.approvalStatus && (
                          <span className={`badge ${
                            organization.approvalStatus === 'APPROVED' ? 'bg-success' :
                            organization.approvalStatus === 'PENDING' ? 'bg-warning text-dark' :
                            organization.approvalStatus === 'REJECTED' ? 'bg-danger' :
                            'bg-secondary'
                          }`}>
                            {organization.approvalStatus}
                          </span>
                        )}
                      </td>
                      <td>
                        {organization?.seatLimit != null
                          ? `${organization.seatLimit}`
                          : 'Unlimited'}
                      </td>
                      <td>
                        <PermissionGuard permission="organization:update">
                          <span
                            className="text-warning"
                            role="button"
                            onClick={() => {
                              tog_large(organization);
                            }}
                          >
                            <FeatherIcon icon="edit" />
                          </span>
                        </PermissionGuard>
                      </td>
                      <td>
                        <PermissionGuard permission="organization:delete">
                          <span className="text-danger" role="button">
                            <FeatherIcon icon="trash-2" />
                          </span>
                        </PermissionGuard>
                      </td>
                    </motion.tr>
                  ))}
                </AnimatePresence>
              </tbody>
            </table>
            {loading && (
              <div className="text-center py-3">
                <Spinner color="primary" />
              </div>
            )}
            {organizations.length === 0 && !loading && (
              <div className="text-center py-5 text-muted">
                {searchTerm
                  ? "No organizations match your search"
                  : "No organizations found"}
              </div>
            )}
            <div className="text-center mt-3">
              {hasMore ? (
                <Button
                  color="primary"
                  onClick={loadMoreOrganizations}
                  disabled={loading}
                >
                  {loading ? "Loading..." : "Load More"}
                </Button>
              ) : (
                organizations.length > 0 && (
                  <div className="text-muted">No more organizations to load</div>
                )
              )}
            </div>
          </div>
        </CardBody>
      </Card>

      <Modal
        size="lg"
        isOpen={modal_large}
        toggle={() => {
          tog_large();
        }}
      >
        <motion.div
          initial={{ scale: 0.95, opacity: 0 }}
          animate={{ scale: 1, opacity: 1 }}
          exit={{ scale: 0.95, opacity: 0 }}
          transition={{ duration: 0.2 }}
        >
          <div className="modal-header">
            <h5 className="modal-title" id="myLargeModalLabel">
              {selectedEntry?.id ? "Edit Organization" : "Add Organization"}
            </h5>
            <button
              onClick={handleModalClose}
              type="button"
              className="btn-close"
              data-dismiss="modal"
              aria-label="Close"
            ></button>
          </div>
          <div className="modal-body">
            <form onSubmit={handleUpdate}>
              <Row className="mb-4">
                <label
                  htmlFor="horizontal-name-input"
                  className="col-sm-3 col-form-label"
                >
                  Profile Picture
                </label>
                <Col sm={9}>
                  <input
                    type="file"
                    className="form-control"
                    id="horizontal-name-input"
                    onChange={handleFileChange}
                  />
                  {errors.profile && (
                    <small className="text-danger">{errors.profile}</small>
                  )}
                </Col>
              </Row>
              <Row className="mb-4">
                <label
                  htmlFor="horizontal-name-input"
                  className="col-sm-3 col-form-label"
                >
                  Name
                </label>
                <Col sm={9}>
                  <input
                    type="text"
                    value={selectedEntry?.name || ""}
                    onChange={(e) =>
                      setSelectedEntry({
                        ...selectedEntry,
                        name: e.target.value,
                      })
                    }
                    className="form-control"
                    placeholder="Enter Name of the Organization"
                    id="horizontal-name-input"
                  />
                  {errors.name && (
                    <small className="text-danger">{errors.name}</small>
                  )}
                </Col>
              </Row>
              <Row className="mb-4">
                <label
                  htmlFor="horizontal-address-input"
                  className="col-sm-3 col-form-label"
                >
                  Address
                </label>
                <Col sm={9}>
                  <input
                    type="text"
                    value={selectedEntry?.address || ""}
                    onChange={(e) =>
                      setSelectedEntry({
                        ...selectedEntry,
                        address: e.target.value,
                      })
                    }
                    className="form-control"
                    placeholder="Enter the Address"
                    id="horizontal-address-input"
                  />
                  {errors.address && (
                    <small className="text-danger">{errors.address}</small>
                  )}
                </Col>
              </Row>
              <Row className="mb-4">
                <label
                  htmlFor="horizontal-address-input"
                  className="col-sm-3 col-form-label"
                >
                  Email
                </label>
                <Col sm={9}>
                  <input
                    type="email"
                    value={selectedEntry?.email || ""}
                    onChange={(e) =>
                      setSelectedEntry({
                        ...selectedEntry,
                        email: e.target.value,
                      })
                    }
                    className="form-control"
                    placeholder="Enter the Email Address"
                    id="horizontal-address-input"
                  />
                  {errors.email && (
                    <small className="text-danger">{errors.email}</small>
                  )}
                </Col>
              </Row>
              <Row className="mb-4">
  <label
    htmlFor="horizontal-contact-input"
    className="col-sm-3 col-form-label"
  >
    Contact Number
  </label>
  <Col sm={9}>
    <div className="d-flex gap-2">
      {/* Country Code Select */}
      <Select
        options={countryOptions}
        value={countryOptions.find((c) =>
          selectedEntry?.contactNumber?.startsWith(
            c.dialCode.replace("+", "")
          )
        )}
        onChange={(selected) => {
          const dialCode = selected.dialCode.replace("+", "");

          const currentCountry = countryOptions.find((c) =>
            selectedEntry?.contactNumber?.startsWith(
              c.dialCode.replace("+", "")
            )
          );
          const prevDialCode = currentCountry
            ? currentCountry.dialCode.replace("+", "")
            : "";
          const phone =
            selectedEntry?.contactNumber?.slice(
              prevDialCode.length
            ) || "";

          setSelectedEntry({
            ...selectedEntry,
            contactNumber: dialCode + phone,
          });
        }}
        filterOption={(option, inputValue) => {
          if (!inputValue) return true;
          const searchText = inputValue.toLowerCase();
          const labelText = option.data.labelText?.toLowerCase() || '';
          const name = option.data.name?.toLowerCase() || '';
          const dialCode = option.data.dialCode?.toLowerCase() || '';
          return labelText.includes(searchText) ||
                name.includes(searchText) ||
                dialCode.includes(searchText);
        }}
        className="flex-grow-0"
        styles={{
          control: (base) => ({
            ...base,
            width: "150px",
          }),
        }}
      />

      {/* Phone Number Input */}
      <input
        type="tel"
        className={`form-control flex-grow-1 ${
          errors.contactNumber ? "is-invalid" : ""
        }`}
        value={(() => {
          const selectedCountry = countryOptions.find((c) =>
            selectedEntry?.contactNumber?.startsWith(
              c.dialCode.replace("+", "")
            )
          );
          const dialCode = selectedCountry
            ? selectedCountry.dialCode.replace("+", "")
            : "";
          return (
            selectedEntry?.contactNumber?.slice(
              dialCode.length
            ) || ""
          );
        })()}
        onChange={(e) => {
          const phone = e.target.value.replace(/\D/g, "");
          const selectedCountry = countryOptions.find((c) =>
            selectedEntry?.contactNumber?.startsWith(
              c.dialCode.replace("+", "")
            )
          );
          const dialCode = selectedCountry
            ? selectedCountry.dialCode.replace("+", "")
            : "";
          setSelectedEntry({
            ...selectedEntry,
            contactNumber: dialCode + phone,
          });
        }}
        placeholder="Phone number"
        id="horizontal-contact-input"
      />
    </div>
    {errors.contactNumber && (
      <small className="text-danger">
        {errors.contactNumber}
      </small>
    )}
  </Col>
</Row>
              {selectedEntry?.id && (
                <Row className="mt-3">
                  <Col lg={6}>
                    <div className="mb-3">
                      <label className="form-label">Seat Limit</label>
                      <Input
                        type="number"
                        min="1"
                        value={selectedEntry?.seatLimit ?? ""}
                        onChange={(e) =>
                          setSelectedEntry({
                            ...selectedEntry,
                            seatLimit: e.target.value ? parseInt(e.target.value, 10) : null,
                          })
                        }
                        placeholder="Unlimited (leave empty)"
                      />
                      <small className="text-muted">Leave empty for unlimited seats</small>
                    </div>
                  </Col>
                </Row>
              )}
              <div className="mt-4">
                {apiError && (
                  <div className="alert alert-danger">{apiError}</div>
                )}
                <button
                  type="submit"
                  className="btn btn-success w-lg transition-all hover-grow pulse-on-hover"
                >
                  {selectedEntry?.id ? "Update" : "Add"}
                </button>
              </div>
            </form>
          </div>
        </motion.div>
      </Modal>

      <Modal
        isOpen={imagePopup.isOpen}
        toggle={closeImagePopup}
        centered
        size="md"
      >
        <div className="modal-header">
          <button
            type="button"
            className="btn-close"
            onClick={closeImagePopup}
            aria-label="Close"
          ></button>
        </div>
        <div className="modal-body text-center">
          <img
            src={imagePopup.imageUrl}
            alt="Organization Profile"
            style={{
              maxWidth: "100%",
              maxHeight: "70vh",
              borderRadius: "8px",
            }}
          />
        </div>
      </Modal>
      <FileModal
        title="Organization"
        isOpen={fileModal}
        toggle={handleClose}
        data={organizations}
        onImport={handleImport}
        onExport={handleExport}
        error={apiError}
        loading={loading}
        importLabel="Import Organizations from Excel"
        exportLabel="Export Organizations to Excel"
      />
    </div>
  );
};

export default Organization;